import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { sitePath } from '@/lib/site-urls';

/**
 * Sitewide floating chat / contact launcher + back-to-top.
 * Portaled to document.body so it sits above page shells after PJAX swaps.
 */
export function FloatingContact() {
  const [open, setOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      setShowTop(y >= 220);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (rootRef.current && target && !rootRef.current.contains(target)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const scrollTop = () => {
    setOpen(false);
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const lenis = (window as Window & { lenis?: { scrollTo: (y: number, opts?: object) => void } }).lenis;
    if (lenis && typeof lenis.scrollTo === 'function') {
      lenis.scrollTo(0, { immediate: prefersReduced });
      return;
    }
    window.scrollTo({ top: 0, behavior: prefersReduced ? 'auto' : 'smooth' });
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <>
      <button
        type="button"
        className={`floating-backtotop${showTop ? ' is-visible' : ''}`}
        aria-label="Back to top"
        title="Back to top"
        onClick={scrollTop}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M12 19V5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M5 12l7-7 7 7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      <div
        ref={rootRef}
        className="floating-contact"
        {...(open ? { 'data-open': '' } : {})}
      >
        <button
          type="button"
          className="floating-contact-toggle"
          aria-label="Contact options"
          aria-expanded={open}
          onClick={(event) => {
            event.stopPropagation();
            setOpen((value) => !value);
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
        <div className="floating-contact-menu">
          <a
            href="https://wa.me/27665733031"
            className="floating-contact-item"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="WhatsApp"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            <span>WhatsApp</span>
          </a>
          <a href="mailto:hello@thinkincode.co.za" className="floating-contact-item" aria-label="Email">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            <span>Email</span>
          </a>
          <a href={sitePath('/contact')} className="floating-contact-item" aria-label="Contact form">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span>Contact</span>
          </a>
          <a
            href="https://www.facebook.com/profile.php?id=61555149453443"
            className="floating-contact-item"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M18 2 h-3 a5 5 0 0 0 -5 5 v3 H7 v4 h3 v8 h4 v-8 h3 l1 -4 h-4 V7 a1 1 0 0 1 1 -1 h3 z" />
            </svg>
            <span>Facebook</span>
          </a>
          <a
            href="https://www.instagram.com/thinkincode.co.za/"
            className="floating-contact-item"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
            </svg>
            <span>Instagram</span>
          </a>
          <a
            href="https://x.com/thinkincode_ZA"
            className="floating-contact-item"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="X"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M18.244 2H21l-6.014 6.874L22 22h-6.6l-5.17-7.53L3.6 22H1l6.46-7.384L0 2h6.76l4.67 6.78L18.244 2Zm-1.16 18h1.83L5.76 3.89H3.8L17.084 20Z" />
            </svg>
            <span>X</span>
          </a>
          <a
            href="https://www.tiktok.com/@thinkincode?_r=1&_t=ZS-95YthLh3znA"
            className="floating-contact-item"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="TikTok"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.7 7.6c-1.1-.7-1.9-1.8-2.1-3.2V3h-3.2v12.2a2.7 2.7 0 1 1-2.2-2.7c.3 0 .5 0 .8.1V9.4a6 6 0 1 0 6 6V9.1c1.1.8 2.5 1.3 4 1.3V7.2c-1.2 0-2.4-.3-3.3-.9Z" />
            </svg>
            <span>TikTok</span>
          </a>
        </div>
      </div>
    </>,
    document.body,
  );
}
